import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssignmentListQuery, ParentOverview } from '@teentack/shared';
import {
  confirmWardCompletion,
  fetchInsights,
  fetchParentOverview,
  fetchWardAssignments,
} from './parent.api';

export const parentKeys = {
  all: ['parent'] as const,
  overview: ['parent', 'overview'] as const,
  assignments: (query: Partial<AssignmentListQuery>) =>
    ['parent', 'assignments', query] as const,
  insights: ['parent', 'insights'] as const,
};

/**
 * The console's single source. Short-lived: an approval answered elsewhere in
 * the app should show up here without a hard reload.
 */
export function useParentOverview(enabled = true) {
  return useQuery<ParentOverview>({
    queryKey: parentKeys.overview,
    queryFn: fetchParentOverview,
    enabled,
    staleTime: 15_000,
  });
}

/** Work belonging to every teen this guardian is verified for. [Parent 14-16] */
export function useWardAssignments(query: Partial<AssignmentListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: parentKeys.assignments(query),
    queryFn: () => fetchWardAssignments(query),
    enabled,
    staleTime: 15_000,
  });
}

/** How it is going, per teen. [Parent step 23] */
export function useInsights(enabled = true) {
  return useQuery({
    queryKey: parentKeys.insights,
    queryFn: fetchInsights,
    enabled,
    staleTime: 60_000,
  });
}

/** Guardian sign-off on finished work. [Parent step 18] */
export function useConfirmWardCompletion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) => confirmWardCompletion(assignmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: parentKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['assignments'] });
    },
  });
}
